import { useId } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Tooltip } from "@similarweb/ui-components";

import { createDomSafeId } from "../helpers/onboardingAgentHelpers";
import type { OnboardingAgentViewportPosition } from "../OnboardingAgent/OnboardingAgent.types";
import { ONBOARDING_AGENT_TRIGGER_ARIA_LABEL, ONBOARDING_AGENT_TRIGGER_TOOLTIP } from "../texts";

import { ONBOARDING_AGENT_TOOLTIP_OPEN_DELAY_MS } from "./constants";
import { useOnboardingAgentTriggerDrag } from "./hooks/useOnboardingAgentTriggerDrag";
import { useTriggerBounceAnimation } from "./hooks/useTriggerBounceAnimation";
import { FixedSlot, IconFrame, TriggerButton } from "./styles";
import { TriggerAiBotIcon } from "./TriggerAiBotIcon";

type OnboardingAgentTriggerProps = {
    position: OnboardingAgentViewportPosition;
    shouldAnimatePosition?: boolean;
    isPanelOpen?: boolean;
    onOpen?: () => void;
    onDragMove?: (position: OnboardingAgentViewportPosition) => void;
    onDragEnd?: (position: OnboardingAgentViewportPosition) => void;
};

const noop = (): void => undefined;

export const OnboardingAgentTrigger = ({
    position,
    shouldAnimatePosition = false,
    isPanelOpen = false,
    onOpen,
    onDragMove,
    onDragEnd,
}: OnboardingAgentTriggerProps) => {
    const { translate } = useTranslate();
    const reactId = useId();
    const gradientId = createDomSafeId("onboarding-agent-trigger-gradient", reactId);
    const { dragPosition, isDragging, handlePointerDown, consumeDragSuppression } = useOnboardingAgentTriggerDrag({
        position,
        onDragMove,
        onDragEnd: onDragEnd ?? noop,
    });
    const isBouncing = useTriggerBounceAnimation(isPanelOpen || isDragging);
    const renderedPosition = dragPosition ?? position;

    const handleClick = (): void => {
        if (consumeDragSuppression()) {
            return;
        }
        onOpen?.();
    };

    return (
        <FixedSlot
            $x={renderedPosition.x}
            $y={renderedPosition.y}
            $shouldAnimatePosition={shouldAnimatePosition}
            $isDragging={isDragging}
            data-automation="onboarding-agent-trigger-root"
        >
            <Tooltip
                content={translate(ONBOARDING_AGENT_TRIGGER_TOOLTIP)}
                placement="left"
                // Controlled shut while dragging instead of disabled: `enabled={false}` drops the
                // tooltip wrapper, which remounts the button and kills the drag mid-gesture.
                open={isDragging ? false : undefined}
                delay={{ open: ONBOARDING_AGENT_TOOLTIP_OPEN_DELAY_MS }}
            >
                <TriggerButton
                    type="button"
                    data-automation="onboarding-agent-trigger-button"
                    aria-label={translate(ONBOARDING_AGENT_TRIGGER_ARIA_LABEL)}
                    onClick={handleClick}
                    onPointerDown={handlePointerDown}
                    $isBouncing={isBouncing}
                    $isDragging={isDragging}
                >
                    <IconFrame>
                        <TriggerAiBotIcon
                            gradientId={gradientId}
                            isBouncing={isBouncing}
                        />
                    </IconFrame>
                </TriggerButton>
            </Tooltip>
        </FixedSlot>
    );
};
